import { HTMLAttributes, useCallback } from 'react';
import { css } from '@emotion/react';
import { DemoChip, demoDataUtils } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { AnimatedBadge } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { ContentLoader, Heading, Text } from '@frontend/design-system';
import { BackgroundConfig, RoiCardBaseProps, SummaryItemProps } from './types';

interface Props extends RoiCardBaseProps {
  isDemoAccount?: boolean;
  trendLine?: number[];
}

const defaultBackgroundConfig: BackgroundConfig = {
  gradientDegree: 135,
  gradientEnd: '#94BFFF', // Color not available in the theme
  gradientStart: theme.colors.primary40,
};

const SummaryItem = ({ label, value, ...rest }: SummaryItemProps & HTMLAttributes<HTMLDivElement>) => (
  <div {...rest}>
    <Text style={{ fontSize: theme.font.size.h1, lineHeight: 1 }} weight='bold'>
      {value}
    </Text>
    <Text>{label}</Text>
  </div>
);

export const SingleRoiCard = ({
  backgroundConfig,
  filteredDate,
  isDemoAccount,
  isLoading,
  onClick,
  summaryValues,
  title,
  trackingId,
  trendLine = [],
}: Props) => {
  const { t } = useTranslation('analytics');
  const background = { ...defaultBackgroundConfig, ...backgroundConfig };

  const keypressHandler = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onClick?.();
    }
  }, []);

  return (
    <section
      css={styles.wrapper}
      data-trackingid={trackingId}
      onClick={onClick}
      onKeyDown={keypressHandler}
      role='button'
      tabIndex={0}
    >
      <div className='stats-container'>
        <header css={styles.flexContainer} style={{ flexWrap: 'wrap' }}>
          <div css={styles.flexContainer} style={{ flexWrap: 'wrap' }}>
            {isDemoAccount ? <DemoChip /> : <AnimatedBadge label={t('New')} />}
            <Heading level={3}>{title}</Heading>
          </div>
          <div css={styles.flexContainer}>
            <Icon name='calendar-small' />
            <Text color='subdued' size='medium'>
              {filteredDate}
            </Text>
          </div>
        </header>
        <div css={[styles.flexContainer, styles.summaryItems]}>
          {summaryValues.map((summaryItem) => (
            <SummaryItem key={summaryItem.label} {...summaryItem} />
          ))}
        </div>
      </div>
      <aside
        className='chart-container'
        style={{
          background: `linear-gradient(${background?.gradientDegree}deg, ${background?.gradientStart}, ${background?.gradientEnd})`,
        }}
      >
        <Chart.TinyAreaChart
          color={theme.colors.white}
          data={isDemoAccount ? demoDataUtils.generateRandomIntArray() : trendLine}
        />
      </aside>
      <ContentLoader show={isLoading} size='small' />
    </section>
  );
};

const styles = {
  wrapper: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    cursor: pointer;
    display: flex;
    overflow: hidden;
    position: relative;
    transition: background-color 0.2s;
    width: 100%;

    &:hover {
      background-color: ${theme.colors.neutral5};
    }

    .stats-container {
      flex: 1;
      gap: ${theme.spacing(3)};
      padding: ${theme.spacing(3)};
    }

    .chart-container {
      flex: 1;
      padding: ${theme.spacing(2)};

      @container (max-width: ${breakpoints.mobile.max}px) {
        display: none;
      }

      @container (min-width: ${breakpoints.mobile.max}px) {
        max-width: 400px;
      }

      @container (min-width: ${breakpoints.large.max}px) {
        max-width: 500px;
      }

      .recharts-surface {
        cursor: pointer;
      }
    }
  `,

  flexContainer: css`
    align-items: center;
    column-gap: ${theme.spacing(1)};
    display: flex;
    justify-content: space-between;
  `,

  summaryItems: css`
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    margin-top: ${theme.spacing(3)};
  `,
};
